// ProfileStack.jsx

import React, { useMemo } from 'react';

// Context
import { useNavigation } from 'context/NavigationContext';
import { useUser } from 'context/UserContext';
import { useBase } from 'context/BaseContext';

// Routing
import { useNavigate } from 'react-router-dom';

// Utilities
import { SectionType } from 'utilities/Enums';
import { getSectionPath } from 'utilities/Routing';

// Services
import { getNode } from 'services/nodes/NodeReadService';

// Hooks
import useNodeOpenIntent from 'views/hooks/useNodeOpenIntent';

// CSS
import './ProfileStack.css';

// Components
import ProfilePhoto from 'components/profilephoto/ProfilePhoto';
import PropTypes from 'prop-types';

/**
 * ProfileStack
 *
 * Renders an overlapping row of profile photos with optional overflow count.
 *
 * @param {boolean} allowNavigation - When true, photo clicks open the member profile.
 * @param {number} maxDisplay - Maximum number of profiles to show before overflow.
 * @param {number} overlap - Pixel overlap between adjacent profile photos.
 * @param {array} profiles - Profile node objects to render.
 * @param {number} size - Profile photo size in pixels.
 *
 * @returns {ReactNode} The rendered profile stack.
 */
const ProfileStack = ({
  profiles = [],
  size = 14,
  maxDisplay = 5,
  overlap,
  allowNavigation = true,
}) => {

  // ----- CONTEXT ----- //

  const { setCurrentNode, setCurrentSection } = useNavigation();
  const { setDisplayedUser } = useUser();
  const { activeBase } = useBase();
  const { openNode } = useNodeOpenIntent();

  // ----- ROUTING ----- //

  const navigate = useNavigate();

  // ----- MEMOS ----- //

  // Visible profiles and overflow count for the current max display setting.

  const { visibleProfiles, overflowCount } = useMemo(() => {
    if (!Array.isArray(profiles) || profiles.length === 0 || maxDisplay <= 0) {
      return { visibleProfiles: [], overflowCount: 0 };
    }

    const clampedMax = Math.max(0, maxDisplay);
    const visible = profiles.slice(0, clampedMax);
    const overflow = Math.max(0, profiles.length - clampedMax);

    return { visibleProfiles: visible, overflowCount: overflow };
  }, [profiles, maxDisplay]);

  // Pixel overlap between stacked profile photos.
  const overlapOffset = useMemo(() => {
    if (typeof overlap === 'number' && Number.isFinite(overlap) && overlap >= 0) {
      return overlap;
    }

    const fallback = 8;
    const parsedSize = Number(size);
    if (!Number.isFinite(parsedSize) || parsedSize <= 0) return fallback;
    return Math.max(4, Math.round(parsedSize * 0.55));
  }, [overlap, size]);

  // ----- HANDLERS ----- //

  /**
   * Opens USER profiles on the profile section; other nodes in Peek.
   *
   * @param {object} event - Click event from the profile item.
   * @param {object} node - Profile node to open.
   */

  const handleNodeClick = async (event, node) => {
    if (!allowNavigation) return;

    event.stopPropagation();

    const fullNode = await getNode(node.key);
    if (!fullNode?.key) {
      return;
    }

    if (fullNode.type === 'USER') {
      const baseKey = String(node?.baseKey || activeBase?.key || '').trim();

      setCurrentNode(fullNode);
      setDisplayedUser(fullNode);
      setCurrentSection(SectionType.USER);

      navigate(
        getSectionPath(
          SectionType.USER,
          baseKey
            ? { baseKey, memberId: fullNode.key }
            : { profileKey: fullNode.key },
        ),
      );
      return;
    }

    openNode(fullNode);
  };

  if (visibleProfiles.length === 0 && overflowCount === 0) {
    return null;
  }

  // ----- RENDER ----- //

  return (
    <div className="profile-stack-container" role="list">
      {visibleProfiles.map((node, index) => (
        <div
          className={`profile-stack-item${allowNavigation ? ' is-navigable' : ''}`}
          style={{
            marginLeft: index === 0 ? 0 : -overlapOffset,
            zIndex: visibleProfiles.length - index,
          }}
          key={node?.key || index}
          role="listitem"
          onClick={allowNavigation ? (event) => handleNodeClick(event, node) : undefined}
        >
          <ProfilePhoto
            node={node}
            size={size}
            borderWidth={0}
            allowNavigation={false}
          />
        </div>
      ))}

      {/* OVERFLOW */}
      {overflowCount > 0 && (

        <div
          className="profile-stack-overflow"
          role="listitem"
        >
          +{overflowCount} Others
        </div>
      )}
    </div>
  );
};

ProfileStack.propTypes = {
  allowNavigation: PropTypes.bool,
  profiles: PropTypes.arrayOf(PropTypes.object),
  size: PropTypes.number,
  maxDisplay: PropTypes.number,
  overlap: PropTypes.number,
};

// Dev Tools
ProfileStack.displayName = 'ProfileStack';

export default ProfileStack;
